// Copyright 2026 - Hewlett Packard Enterprise Company
import { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  getAuthority,
  OIDC_CLIENT_ID,
  WORKSPACE_ID_KEY,
  TENANT_ACID_KEY,
  PENDING_TENANT_ACID_KEY,
} from '../../constants';
import { clearAuthStorage } from '../../utils/authHelpers';

/**
 * This component intercepts region switch navigation and clears stale session
 * data before AuthProvider initializes. When switching regions, each region has
 * its own applicationCustomerId, so we must discard the stale session and cid
 * to force GLCP to resolve the correct one for the new region.
 */
const RegionSwitchInterceptor = ({ children }) => {
  const hasProcessed = useRef(false);

  // Only process once per mount to avoid re-processing on re-renders
  if (!hasProcessed.current) {
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const isRegionSwitch =
      params.get('region_switch') === 'true' && params.has('workspace_id');

    if (isRegionSwitch) {
      // Discard all stale auth storage to resolve fresh context for the new region
      clearAuthStorage();

      // workspace_id is required on region switch and identifies the target region's workspace
      const workspaceId = params.get('workspace_id');
      localStorage.setItem(WORKSPACE_ID_KEY, workspaceId);

      // clear any existing session for the workspace
      const sessionKey = `oidc.user:${getAuthority(workspaceId)}:${OIDC_CLIENT_ID}`;
      sessionStorage.removeItem(sessionKey);

      // Strip region_switch params from URL so they don't persist or affect future navigation
      params.delete('region_switch');
      params.delete('workspace_id');
      const newSearch = params.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      window.history.replaceState(window.history.state ?? {}, '', newUrl);
    } else {
      // Email links pass tenant_acid in the URL to restore tenant context.
      // Store in PENDING key — AccountProvider will promote to active only
      // after confirming this is an MSP workspace. This prevents 403 errors
      // from sending tenant-acid header to non-MSP workspaces.
      const queryTenantAcid = params.get('tenant_acid');
      if (queryTenantAcid) {
        localStorage.setItem(PENDING_TENANT_ACID_KEY, queryTenantAcid);

        // Clear any existing active tenant acid so the request interceptor
        // does not send a stale tenant-acid header before AccountProvider
        // promotes the pending value (MSP) or discards it (non-MSP). For MSP
        // the pending acid is promoted back to active, so nothing is lost.
        localStorage.removeItem(TENANT_ACID_KEY);
        params.delete('tenant_acid');
        const newSearch = params.toString();
        const newUrl = newSearch
          ? `${window.location.pathname}?${newSearch}`
          : window.location.pathname;
        window.history.replaceState(window.history.state ?? {}, '', newUrl);
      }
    }
  }

  return children;
};

RegionSwitchInterceptor.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RegionSwitchInterceptor;
